/**
 * Jednotné zobrazení čísel, PSČ a zemí pro člověka (KAN-2017, KAN-2018).
 *
 * Test21 našel „do 31.5 kg", „80.0 h/týden", „317.7 mm", „2,1033 CZK",
 * PSČ „11000" a zemi jednou „CZ", jinde „Česko" a „Česká republika".
 * Každá obrazovka si to formátovala sama (``toFixed``, holý řetězec
 * z API). Tady je jedno místo — obrazovky volají tyto funkce, nikdy
 * ``toFixed`` ani syrový kód země.
 *
 * Jen zobrazení: editační pole drží hodnotu tak, jak přišla.
 */

/** Úzká nezlomitelná mezera — mezi číslem a jednotkou („31,5 kg"). */
const NBSP = " ";

export interface NumberFormatOptions {
  /** Jazyk čtenáře (``i18n.language``); výchozí čeština. */
  locale?: string;
  /** Nejvýš tolik desetinných míst (výchozí 2). Koncové nuly se zahazují. */
  maxFractionDigits?: number;
  /** Aspoň tolik desetinných míst (výchozí 0) — pro pevný tvar „1,20". */
  minFractionDigits?: number;
}

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Číslo v jazyce čtenáře: desetinná čárka v češtině, skupiny tisíců,
 * rozumný počet desetinných míst (výchozí nejvýš 2).
 *
 * ``formatNumber(31.5)`` → „31,5"; ``formatNumber(80)`` → „80";
 * ``formatNumber(2.1033)`` → „2,1"; ``formatNumber(12500)`` → „12 500".
 * Nečíselný vstup se vrací beze změny (prázdný → ""), nikdy „NaN".
 */
export function formatNumber(value: unknown, options: NumberFormatOptions = {}): string {
  const n = toFiniteNumber(value);
  if (n === null) return value === null || value === undefined ? "" : String(value);
  const max = Math.max(options.maxFractionDigits ?? 2, options.minFractionDigits ?? 0);
  return new Intl.NumberFormat(options.locale ?? "cs-CZ", {
    minimumFractionDigits: options.minFractionDigits ?? 0,
    maximumFractionDigits: max,
  }).format(n);
}

/**
 * Číslo s jednotkou, oddělené nezlomitelnou mezerou: „31,5 kg",
 * „317,7 mm", „1,5 mm", „80 h/týden". Jednotka se nepřekládá — volající
 * ji dá už přeloženou (nebo SI značku).
 */
export function formatQuantity(
  value: unknown,
  unit: string,
  options: NumberFormatOptions = {},
): string {
  const number = formatNumber(value, options);
  if (!number) return "";
  return unit ? `${number}${NBSP}${unit}` : number;
}

/**
 * Tvar PSČ podle země. Česko a Slovensko „110 00", Polsko „00-950";
 * ostatní země (a cokoli, co do vzoru nesedí) beze změny — špatně
 * přeformátovaná adresa je horší než neformátovaná.
 */
const POSTAL_PATTERNS: Record<string, (digits: string) => string | null> = {
  CZ: (d) => (/^\d{5}$/.test(d) ? `${d.slice(0, 3)} ${d.slice(3)}` : null),
  SK: (d) => (/^\d{5}$/.test(d) ? `${d.slice(0, 3)} ${d.slice(3)}` : null),
  PL: (d) => (/^\d{5}$/.test(d) ? `${d.slice(0, 2)}-${d.slice(2)}` : null),
};

export function formatPostalCode(
  postalCode: string | null | undefined,
  countryCode: string | null | undefined,
): string {
  if (!postalCode) return "";
  const trimmed = postalCode.trim();
  const pattern = POSTAL_PATTERNS[(countryCode ?? "").trim().toUpperCase()];
  if (!pattern) return trimmed;
  return pattern(trimmed.replace(/[\s-]/g, "")) ?? trimmed;
}

/**
 * Lidský název země z ISO kódu v jazyce čtenáře — „CZ" → „Česko"
 * (cs), „Czechia" (en). Jedno jméno všude (KAN-2018): žádné „CZ" ani
 * „Česká republika" vedle sebe.
 *
 * Přijme i název místo kódu (starší data, volný text) — vrátí ho beze
 * změny, jen dlouhý český tvar sjednotí na „Česko". Neznámý kód se
 * vrací tak, jak přišel.
 */
const COUNTRY_NAME_ALIASES: Record<string, string> = {
  "česká republika": "CZ",
  "ceska republika": "CZ",
  "czech republic": "CZ",
  "slovenská republika": "SK",
};

export function countryName(
  country: string | null | undefined,
  locale: string = "cs",
): string {
  if (!country) return "";
  const trimmed = country.trim();
  const code = /^[A-Za-z]{2}$/.test(trimmed)
    ? trimmed.toUpperCase()
    : COUNTRY_NAME_ALIASES[trimmed.toLowerCase()];
  if (!code) return trimmed;
  try {
    const name = new Intl.DisplayNames([locale], { type: "region" }).of(code);
    return name && name !== code ? name : trimmed;
  } catch {
    return trimmed;
  }
}

export interface AddressLike {
  street?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
  country_code?: string | null;
}

/**
 * Řádek „PSČ Město" s PSČ ve tvaru země — „110 00 Praha". Pro země,
 * které píší PSČ za město, to pořadí volající určí sám
 * (``postalCodeBeforeCity`` v ``addressFormat.ts``).
 */
export function formatPostalCity(address: AddressLike): string {
  const country = address.country_code ?? address.country ?? null;
  const postal = formatPostalCode(address.postal_code, country);
  return [postal, address.city?.trim()].filter(Boolean).join(" ");
}
